import { Controller } from '@hotwired/stimulus'

/**
 * Базовые параметры desktop-стопки.
 *
 * `BASE_*` применяется к каждому шагу progress, а `EXTRA_*` добавляется
 * только начиная со второго неактивного шага: третий слайд получает одну
 * добавку, четвертый — две и так далее.
 */
const BASE_OFFSET_X = 209
const BASE_OFFSET_Y = 71
const EXTRA_OFFSET_X = 63
const EXTRA_OFFSET_Y = 53

/**
 * Параметры "тянущегося" движения во время drag.
 *
 * Используются поверх creativeEffect, чтобы активная карточка чуть следовала
 * за пальцем или мышью, но не уезжала настолько далеко, чтобы ломать стопку.
 */
const DRAG_PULL_RATIO = 0.18
const DRAG_PULL_MAX = 72
const DRAG_LIFT_RATIO = 0.04
const DRAG_LIFT_MAX = 16
const DRAG_ROTATE_RATIO = 0.025
const DRAG_ROTATE_MAX = 6
const DRAG_SCALE = 0.03

/**
 * Параметры промежуточного "нырка" карточки под стопку.
 *
 * Эффект работает только пока карточка переходит между active и inactive:
 * на середине движения она чуть выносится наружу, затем возвращается в
 * обычное положение неактивной карточки.
 */
const TUCK_OFFSET_X = 96
const TUCK_LIFT_Y = 28
const TUCK_ROTATE = 7
const TUCK_SCALE = 0.04

/**
 * Лёгкий desktop-hover: неактивные карточки чуть расходятся по сторонам.
 */
const HOVER_OFFSET_X = 8
const HOVER_TRANSITION_DURATION = 300
const HOVER_TRANSITION_MAX_DELAY = 150
const HOVER_TRANSITION_TIMING = 'ease-in-out'

/**
 * @typedef {Object} MotionEffect
 * @property {number} x Дополнительное смещение по X в пикселях.
 * @property {number} y Дополнительное смещение по Y в пикселях.
 * @property {number} rotate Дополнительный поворот по Z в градусах.
 * @property {number} scale Дополнительный множитель масштаба.
 */

/**
 * Контроллер слайдера преимуществ на главной странице.
 *
 * Использует Swiper creativeEffect как основу, но переопределяет transform
 * каждого слайда в событии `setTranslate`, чтобы получить нестандартную
 * стопку карточек:
 *
 * - на desktop следующие карточки лежат слева, предыдущие — справа;
 * - дальние карточки получают дополнительный нелинейный offset;
 * - во время drag карточка слегка тянется за курсором;
 * - при уходе из active карточка делает короткое движение "под стопку";
 * - на mobile включается loop и упрощенная геометрия.
 */
export default class BenefitsSliderController extends Controller {
  static values = {
    prevText: {
      type: String
    },
    nextText: {
      type: String
    },
    bulletText: {
      type: String
    }
  }

  static targets = [
    'container',
    'navigation',
    'prevBtn',
    'nextBtn',
    'pagination'
  ]

  #swiper = null
  #connectToken = null
  #desktopMediaQuery = null
  #desktopMediaQueryHandler = null
  #isHovered = false
  #hoverAbortController = null
  #hoverTransitionTimeout = null
  #animationId = null
  #isAnimating = false
  // { from: number[], duration: number } | null — запоминает параметры ожидающей RAF-анимации
  #pendingAnimation = null
  // Последнее отрисованное состояние — единственный надёжный источник FROM-прогресса
  #lastRenderedProgress = null

  // Кеш импорта Swiper — загружается один раз
  static #swiperCache = null

  /**
   * Инициализирует Swiper и навешивает кастомный пересчет transform.
   *
   * `touchRatio: -1` нужен потому, что визуально next-стопка находится слева,
   * а стандартный Swiper считает переход к next движением в противоположную
   * сторону.
   *
   * @returns {Promise<void>}
   */
  async connect() {
    const connectToken = Symbol('benefits-slider-connect')
    this.#connectToken = connectToken

    const { Swiper, Navigation, Pagination, A11y, EffectCreative } =
      await this.#importSwiper()

    if (this.#connectToken !== connectToken) return

    this.#desktopMediaQuery = window.matchMedia('(min-width: 1024px)')

    const limitProgress = Math.max(
      this.containerTarget.querySelectorAll('.swiper-slide').length - 1,
      1
    )

    const breakpoints = {
      320: {
        creativeEffect: {
          prev: {
            translate: [0, 0, 0],
            rotate: [0, 0, 4.5]
          },
          next: {
            translate: [0, 0, 0],
            rotate: [0, 0, 4.5]
          },
          limitProgress: 2
        }
      },
      1024: {
        creativeEffect: {
          prev: {
            translate: [BASE_OFFSET_X, BASE_OFFSET_Y, 0],
            rotate: [0, 0, 15]
          },
          next: {
            translate: [-BASE_OFFSET_X, BASE_OFFSET_Y, 0],
            rotate: [0, 0, -15]
          },
          limitProgress
        },
        grabCursor: true
      }
    }

    const initializeSwiper = () => {
      const initialSlide = this.#swiper?.realIndex ?? 0

      this.#destroySwiper()
      this.#swiper = new Swiper(this.containerTarget, {
        modules: [Navigation, Pagination, A11y, EffectCreative],
        speed: 800,
        slidesPerView: 'auto',
        spaceBetween: 0,
        allowTouchMove: true,
        touchRatio: -1,
        effect: 'creative',
        loop: !this.#desktopMediaQuery.matches,
        initialSlide,
        creativeEffect: {
          perspective: false
        },
        breakpoints,
        a11y: {
          prevSlideMessage: this.prevTextValue,
          nextSlideMessage: this.nextTextValue
        },
        navigation: {
          nextEl: this.hasNextBtnTarget ? this.nextBtnTarget : null,
          prevEl: this.hasPrevBtnTarget ? this.prevBtnTarget : null
        },
        pagination: this.hasPaginationTarget
          ? {
              el: this.paginationTarget,
              clickable: true,
              renderBullet: (index, className) =>
                `<button type="button" class="${className}" aria-label="${this.bulletTextValue} ${index + 1}"></button>`
            }
          : null,
        on: {
          setTranslate: (swiper) => this.#onSetTranslate(swiper),
          setTransition: (swiper, duration) =>
            this.#onSetTransition(swiper, duration)
        }
      })
    }

    this.#desktopMediaQueryHandler = () => {
      if (this.#connectToken !== connectToken) return

      initializeSwiper()
    }
    this.#desktopMediaQuery.addEventListener(
      'change',
      this.#desktopMediaQueryHandler
    )
    this.#addHoverHandlers()
    initializeSwiper()
  }

  disconnect() {
    this.#connectToken = null
    this.#isHovered = false
    this.#removeHoverHandlers()
    this.#removeDesktopMediaQueryHandler()
    this.#destroySwiper()
  }

  #removeDesktopMediaQueryHandler() {
    if (this.#desktopMediaQuery && this.#desktopMediaQueryHandler) {
      this.#desktopMediaQuery.removeEventListener(
        'change',
        this.#desktopMediaQueryHandler
      )
    }

    this.#desktopMediaQuery = null
    this.#desktopMediaQueryHandler = null
  }

  #destroySwiper() {
    this.#cancelAnimation()
    this.#cancelHoverTransitionCleanup()
    this.#pendingAnimation = null
    this.#lastRenderedProgress = null
    this.#swiper?.destroy(true, true)
    this.#swiper = null
  }

  #addHoverHandlers() {
    if (this.#hoverAbortController) return

    this.#hoverAbortController = new AbortController()
    const { signal } = this.#hoverAbortController

    this.containerTarget.addEventListener(
      'pointerenter',
      () => this.#setHoverState(true),
      { signal }
    )
    this.containerTarget.addEventListener(
      'pointerleave',
      () => this.#setHoverState(false),
      { signal }
    )
  }

  #removeHoverHandlers() {
    this.#hoverAbortController?.abort()
    this.#hoverAbortController = null
  }

  #setHoverState(isHovered) {
    const nextIsHovered = isHovered && this.#canUseHoverMotion()

    if (this.#isHovered === nextIsHovered) return

    this.#isHovered = nextIsHovered

    if (!this.#swiper || this.#swiper.destroyed || this.#isAnimating) return

    this.#applyHoverTransition(this.#swiper)
    this.#setStackedSlidesTransform(this.#swiper)
  }

  #canUseHoverMotion() {
    return (
      this.#desktopMediaQuery?.matches === true &&
      window.matchMedia('(hover: hover) and (pointer: fine)').matches
    )
  }

  #applyHoverTransition(swiper) {
    this.#cancelHoverTransitionCleanup()

    const distanceFor = (slide) =>
      Math.min(
        Math.abs(slide.progress),
        swiper.params.creativeEffect.limitProgress
      )
    const maxDistance = Math.max(...Array.from(swiper.slides, distanceFor), 1)

    swiper.slides.forEach((slide) => {
      const delay =
        (distanceFor(slide) / maxDistance) * HOVER_TRANSITION_MAX_DELAY

      this.#slideTransformTarget(slide).style.transition =
        `transform ${HOVER_TRANSITION_DURATION}ms ${HOVER_TRANSITION_TIMING} ${delay}ms`
    })

    this.#hoverTransitionTimeout = window.setTimeout(() => {
      this.#hoverTransitionTimeout = null

      if (this.#swiper !== swiper || swiper.destroyed || this.#isAnimating) {
        return
      }

      this.#clearHoverTransition(swiper)
    }, HOVER_TRANSITION_DURATION + HOVER_TRANSITION_MAX_DELAY)
  }

  #clearHoverTransition(swiper) {
    swiper.slides.forEach((slide) => {
      this.#slideTransformTarget(slide).style.transition = ''
    })
  }

  #cancelHoverTransitionCleanup() {
    if (this.#hoverTransitionTimeout === null) return

    window.clearTimeout(this.#hoverTransitionTimeout)
    this.#hoverTransitionTimeout = null

    if (this.#swiper && !this.#swiper.destroyed) {
      this.#clearHoverTransition(this.#swiper)
    }
  }

  /**
   * Пересчитывает transform каждого слайда поверх Swiper creativeEffect.
   *
   * Swiper сам обновляет `slideEl.progress`; здесь этот progress превращается
   * в итоговые translate/rotate/scale/opacity с учетом слоев движения:
   * базовой стопки, hover, drag-эффекта и tuck-эффекта.
   *
   * @param {import('swiper').Swiper} swiper Экземпляр Swiper.
   * @returns {void}
   */
  #setStackedSlidesTransform(swiper, progressMap = null) {
    const params = swiper.params.creativeEffect
    const multiplier = params.progressMultiplier
    const useHoverMotion = this.#isHovered && this.#canUseHoverMotion()

    swiper.slides.forEach((slideEl, index) => {
      const rawProgress =
        progressMap !== null
          ? (progressMap[index] ?? slideEl.progress)
          : slideEl.progress
      const progress = this.#clamp(
        rawProgress,
        -params.limitProgress,
        params.limitProgress
      )
      const absoluteProgress = Math.abs(progress * multiplier)
      const extraProgress = Math.max(absoluteProgress - 1, 0)
      const originalProgress = this.#clamp(
        progressMap !== null
          ? rawProgress
          : (slideEl.originalProgress ?? rawProgress),
        -params.limitProgress,
        params.limitProgress
      )
      const effect = this.#effectForProgress(params, progress)
      const slideIndex =
        swiper.virtual && swiper.params.virtual.enabled
          ? swiper.virtual.from + index
          : index
      const drag = this.#dragEffectForSlide(swiper, slideIndex, progress)
      const tuck = this.#tuckEffectForSlide(swiper, slideIndex, progress)
      const hoverX = this.#hoverOffsetForProgress(progress, useHoverMotion)
      const offset = swiper.params.cssMode
        ? -slideEl.swiperSlideOffset - swiper.translate
        : -slideEl.swiperSlideOffset
      const translate = [offset, 0, 0]

      if (!swiper.isHorizontal()) {
        translate[1] = translate[0]
        translate[0] = 0
      }

      const isMobile = parseFloat(swiper.currentBreakpoint) < 1024

      translate[0] = this.#translateValue(
        translate[0],
        effect.translate[0],
        absoluteProgress,
        isMobile ? 0 : effect.extraDirectionX * EXTRA_OFFSET_X,
        isMobile ? 0 : extraProgress,
        hoverX + drag.x + tuck.x
      )
      translate[1] = this.#translateValue(
        translate[1],
        effect.translate[1],
        absoluteProgress,
        isMobile ? 0 : effect.extraDirectionY * EXTRA_OFFSET_Y,
        isMobile ? 0 : extraProgress,
        drag.y + tuck.y
      )
      translate[2] = this.#translateValue(
        translate[2],
        effect.translate[2],
        absoluteProgress,
        0,
        isMobile ? 0 : extraProgress
      )

      const rotateProgress = isMobile
        ? Math.max(absoluteProgress, Math.floor(Math.abs(rawProgress)))
        : absoluteProgress
      const rotate = effect.rotate.map((value) => value * rotateProgress)
      rotate[2] += drag.rotate + tuck.rotate

      const scale =
        this.#scaleValue(effect.scale, originalProgress, multiplier) *
        drag.scale *
        tuck.scale
      const opacity = this.#clamp(
        1 - Math.max(absoluteProgress - 1, 0) * 0.5,
        0.5,
        1
      )
      const targetEl = this.#slideTransformTarget(slideEl)

      targetEl.style.transform = [
        `translate3d(${translate.join(', ')})`,
        `rotateX(${rotate[0]}deg)`,
        `rotateY(${rotate[1]}deg)`,
        `rotateZ(${rotate[2]}deg)`,
        `scale(${scale})`
      ].join(' ')
      slideEl.style.setProperty('--slide-opacity', opacity)
      slideEl.style.setProperty(
        '--overlay-opacity',
        this.#clamp(absoluteProgress, 0, 1)
      )
    })

    // Сохраняем progress, который только что отрисовали. Это единственный
    // надёжный способ знать визуальное FROM-состояние для следующей анимации:
    // slide.progress обновляется до setTransition/setTranslate в slideTo,
    // поэтому в обработчиках событий всегда уже новое значение.
    this.#lastRenderedProgress =
      progressMap !== null
        ? [...progressMap]
        : Array.from(swiper.slides, (s) => s.progress)
  }

  #hoverOffsetForProgress(progress, useHoverMotion) {
    if (!useHoverMotion) return 0

    return (
      Math.sign(progress) * HOVER_OFFSET_X * Math.min(Math.abs(progress), 1)
    )
  }

  /**
   * Возвращает набор creativeEffect-параметров для текущей стороны progress.
   *
   * В Swiper отрицательный progress соответствует next-слайдам, положительный —
   * prev-слайдам. Дополнительные направления нужны для нелинейной desktop-
   * добавки `EXTRA_OFFSET_*`.
   *
   * @param {Object} params Текущие параметры `swiper.params.creativeEffect`.
   * @param {number} progress Ограниченный progress слайда.
   * @returns {Object} Параметры transform и направления дополнительных offsets.
   */
  #effectForProgress(params, progress) {
    if (progress < 0) {
      return {
        ...params.next,
        extraDirectionX: 1,
        extraDirectionY: 1
      }
    }

    if (progress > 0) {
      return {
        ...params.prev,
        extraDirectionX: -1,
        extraDirectionY: 1
      }
    }

    return {
      translate: [0, 0, 0],
      rotate: [0, 0, 0],
      opacity: 1,
      scale: 1,
      extraDirectionX: 0,
      extraDirectionY: 0
    }
  }

  /**
   * Считает промежуточное движение карточки, которая уходит под стопку.
   *
   * Эффект активен только при `0 < abs(progress) < 1`, то есть пока карточка
   * еще не стала полноценной неактивной карточкой. Амплитуда максимальна около
   * середины перехода и равна нулю в начале и конце.
   *
   * @param {import('swiper').Swiper} swiper Экземпляр Swiper.
   * @param {number} slideIndex Индекс слайда с учетом virtual-режима.
   * @param {number} progress Ограниченный progress слайда.
   * @returns {MotionEffect} Дополнительные transform-значения.
   */
  #tuckEffectForSlide(swiper, slideIndex, progress) {
    const absoluteProgress = Math.abs(progress)

    if (absoluteProgress <= 0 || absoluteProgress >= 1) {
      return this.#defaultMotionEffect()
    }

    const isMovingToPrevStack =
      progress > 0 &&
      (slideIndex === swiper.activeIndex ||
        slideIndex === swiper.activeIndex - 1)
    const isMovingToNextStack =
      progress < 0 &&
      (slideIndex === swiper.activeIndex ||
        slideIndex === swiper.activeIndex + 1)

    if (!isMovingToPrevStack && !isMovingToNextStack) {
      return this.#defaultMotionEffect()
    }

    const direction = progress > 0 ? 1 : -1
    const influence = this.#midProgressInfluence(absoluteProgress)

    return {
      x: direction * TUCK_OFFSET_X * influence,
      y: -TUCK_LIFT_Y * influence,
      rotate: direction * TUCK_ROTATE * influence,
      scale: 1 - TUCK_SCALE * influence
    }
  }

  /**
   * Считает "тянущееся" движение карточки во время пользовательского drag.
   *
   * Движение строится от реальной дистанции пальца/мыши, а не от
   * `swiper.translate`, потому что translate инвертирован через `touchRatio`.
   *
   * @param {import('swiper').Swiper} swiper Экземпляр Swiper.
   * @param {number} slideIndex Индекс слайда с учетом virtual-режима.
   * @param {number} progress Ограниченный progress слайда.
   * @returns {MotionEffect} Дополнительные transform-значения.
   */
  #dragEffectForSlide(swiper, slideIndex, progress) {
    const { isTouched, startTranslate } = swiper.touchEventsData

    if (!isTouched || Math.abs(progress) >= 1) {
      return this.#defaultMotionEffect()
    }

    const currentTranslate = swiper.rtlTranslate
      ? -swiper.translate
      : swiper.translate
    const dragDistance = this.#dragDistance(swiper)
    const isSwipeToNext =
      (slideIndex === swiper.activeIndex ||
        slideIndex === swiper.activeIndex - 1) &&
      progress > 0 &&
      currentTranslate < startTranslate
    const isSwipeToPrev =
      (slideIndex === swiper.activeIndex ||
        slideIndex === swiper.activeIndex + 1) &&
      progress < 0 &&
      currentTranslate > startTranslate

    if (!isSwipeToNext && !isSwipeToPrev) {
      return this.#defaultMotionEffect()
    }

    const influence = this.#midProgressInfluence(Math.abs(progress))
    const pull = this.#clamp(
      dragDistance * DRAG_PULL_RATIO,
      -DRAG_PULL_MAX,
      DRAG_PULL_MAX
    )

    return {
      x: pull * influence,
      y:
        -Math.min(Math.abs(dragDistance) * DRAG_LIFT_RATIO, DRAG_LIFT_MAX) *
        influence,
      rotate:
        this.#clamp(
          dragDistance * DRAG_ROTATE_RATIO,
          -DRAG_ROTATE_MAX,
          DRAG_ROTATE_MAX
        ) * influence,
      scale: 1 - DRAG_SCALE * influence
    }
  }

  /**
   * Возвращает нейтральный слой движения.
   *
   * @returns {MotionEffect} Нулевой offset, поворот и единичный scale.
   */
  #defaultMotionEffect() {
    return {
      x: 0,
      y: 0,
      rotate: 0,
      scale: 1
    }
  }

  /**
   * Возвращает коэффициент влияния, максимальный в середине перехода.
   *
   * При progress 0 и 1 возвращает 0, при progress 0.5 возвращает 1.
   *
   * @param {number} progress Абсолютный progress в диапазоне от 0 до 1.
   * @returns {number} Коэффициент влияния от 0 до 1.
   */
  #midProgressInfluence(progress) {
    return Math.sqrt(1 - Math.min(Math.abs((progress - 0.5) / 0.5), 1))
  }

  /**
   * Возвращает реальную дистанцию drag по активной оси.
   *
   * @param {import('swiper').Swiper} swiper Экземпляр Swiper.
   * @returns {number} Смещение указателя от начала drag в пикселях.
   */
  #dragDistance(swiper) {
    const touches = swiper.touches

    if (swiper.isHorizontal()) {
      return touches.currentX - touches.startX
    }

    return touches.currentY - touches.startY
  }

  /**
   * Собирает CSS `calc()` для translate-координаты.
   *
   * @param {number} base Базовый offset слайда от Swiper.
   * @param {number|string} offset Offset creativeEffect для одного шага progress.
   * @param {number} progress Абсолютный progress с учетом multiplier.
   * @param {number} extraOffset Нелинейная добавка для дальних карточек.
   * @param {number} extraProgress Количество дополнительных шагов после первого.
   * @param {number} [dragOffset=0] Суммарная drag/tuck-добавка в пикселях.
   * @returns {string} CSS-значение для одной координаты translate.
   */
  #translateValue(
    base,
    offset,
    progress,
    extraOffset,
    extraProgress,
    dragOffset = 0
  ) {
    return `calc(${base}px + (${this.#cssValue(offset)} * ${progress}) + (${extraOffset}px * ${extraProgress}) + ${dragOffset}px)`
  }

  /**
   * Приводит число или CSS-строку к значению, пригодному для `calc()`.
   *
   * @param {number|string} value Значение translate из creativeEffect.
   * @returns {string} CSS-значение с единицей измерения.
   */
  #cssValue(value) {
    return typeof value === 'string' ? value : `${value}px`
  }

  /**
   * Считает scale так же, как Swiper creativeEffect.
   *
   * @param {number} scale Целевой scale из creativeEffect.
   * @param {number} progress Original progress слайда.
   * @param {number} multiplier Множитель progress из creativeEffect.
   * @returns {number} Итоговый scale.
   */
  #scaleValue(scale, progress, multiplier) {
    if (progress < 0) return 1 + (1 - scale) * progress * multiplier

    return 1 - (1 - scale) * progress * multiplier
  }

  /**
   * Возвращает элемент, на который должен применяться transform.
   *
   * Повторяет логику Swiper: если внутри слайда есть `.swiper-slide-transform`,
   * transform применяется к нему, иначе к самому слайду.
   *
   * @param {HTMLElement} slideEl DOM-элемент слайда.
   * @returns {HTMLElement} Элемент для transform.
   */
  #slideTransformTarget(slideEl) {
    const targetEl =
      slideEl.querySelector('.swiper-slide-transform') ||
      slideEl.shadowRoot?.querySelector('.swiper-slide-transform') ||
      slideEl

    if (targetEl !== slideEl) {
      targetEl.style.backfaceVisibility = 'hidden'
      targetEl.style['-webkit-backface-visibility'] = 'hidden'
    }

    return targetEl
  }

  /**
   * Ограничивает число заданным диапазоном.
   *
   * @param {number} value Исходное значение.
   * @param {number} min Нижняя граница.
   * @param {number} max Верхняя граница.
   * @returns {number} Значение внутри диапазона `[min, max]`.
   */
  #clamp(value, min, max) {
    return Math.min(Math.max(value, min), max)
  }

  /**
   * Обработчик `setTranslate`.
   *
   * Swiper вызывает его каждый раз, когда обновляет позицию.
   *
   * Ключевой факт (из swiper-core.mjs): `updateProgress` вызывается ДО
   * `setTransition` и `setTranslate` в `slideTo`. Поэтому `slide.progress`
   * в обоих обработчиках уже содержит НОВЫЕ (финальные) значения.
   * Единственный надёжный способ знать FROM-состояние — хранить то,
   * что мы сами последний раз отрисовали в `#lastRenderedProgress`.
   *
   * @param {import('swiper').Swiper} swiper Экземпляр Swiper.
   * @returns {void}
   */
  #onSetTranslate(swiper) {
    if (this.#pendingAnimation !== null) {
      const { from, duration } = this.#pendingAnimation
      this.#pendingAnimation = null
      const to = Array.from(swiper.slides, (s) => s.progress)
      if (from.some((f, i) => Math.abs(f - to[i]) > 0.001)) {
        this.#startNavAnimation(swiper, from, to, duration)
        return
      }
    }

    // Если идёт RAF-анимация, но pendingAnimation нет — значит пришёл
    // мгновенный setTranslate (duration=0: loop-fix, ресайз и т.п.).
    // Отменяем анимацию и рендерим текущее состояние напрямую.
    if (this.#isAnimating) this.#cancelAnimation()
    this.#setStackedSlidesTransform(swiper)
  }

  /**
   * Обработчик `setTransition`.
   *
   * Здесь `slide.progress` уже содержит НОВЫЕ значения (см. #onSetTranslate),
   * поэтому FROM берём из `#lastRenderedProgress` — последнего отрисованного
   * состояния, которое и есть то, что видит пользователь прямо сейчас.
   *
   * @param {import('swiper').Swiper} swiper Экземпляр Swiper.
   * @param {number} duration Длительность анимации в мс.
   * @returns {void}
   */
  #onSetTransition(swiper, duration) {
    this.#cancelHoverTransitionCleanup()

    if (duration === 0) return

    // #lastRenderedProgress — это то, что сейчас на экране: при drag он
    // обновляется на каждом кадре, при RAF-анимации — тоже. Это надёжный FROM.
    this.#pendingAnimation = {
      from: this.#lastRenderedProgress
        ? [...this.#lastRenderedProgress]
        : Array.from(swiper.slides, (s) => s.progress),
      duration
    }
  }

  /**
   * Запускает RAF-анимацию перехода между `fromProgress` и `toProgress`.
   *
   * CSS-transitions на слайдах подавляются — вместо них работает наш RAF-цикл,
   * который вызывает `#setStackedSlidesTransform` с интерполированным progressMap
   * на каждом кадре. Это позволяет tuck-эффекту пиковать в середине перехода
   * точно так же, как при drag.
   *
   * Wrapper (`wrapperEl`) не трогается: в EffectCreative `virtualTranslate: true`,
   * поэтому wrapper всегда остаётся на нулевой позиции.
   *
   * @param {import('swiper').Swiper} swiper Экземпляр Swiper.
   * @param {number[]} fromProgress Массив progress для начального состояния.
   * @param {number[]} toProgress Массив progress для конечного состояния.
   * @param {number} duration Длительность анимации в мс.
   * @returns {void}
   */
  #startNavAnimation(swiper, fromProgress, toProgress, duration) {
    this.#cancelHoverTransitionCleanup()

    // Подавляем CSS-transitions, выставленные EffectCreative.
    swiper.slides.forEach((slide) => {
      this.#slideTransformTarget(slide).style.transitionDuration = '0ms'
    })

    this.#cancelAnimation()
    this.#isAnimating = true

    // Рендерим FROM-состояние немедленно, чтобы не было прыжка к финальному.
    this.#setStackedSlidesTransform(swiper, fromProgress)

    const startTime = performance.now()

    const animate = (now) => {
      const t = Math.min((now - startTime) / duration, 1)
      const eased = this.#easeOutCubic(t)
      const progressMap = fromProgress.map(
        (from, i) => from + (toProgress[i] - from) * eased
      )
      this.#setStackedSlidesTransform(swiper, progressMap)

      if (t < 1) {
        this.#animationId = requestAnimationFrame(animate)
      } else {
        this.#isAnimating = false
        this.#animationId = null
        // Разблокируем swiper.animating: EffectCreative ждал CSS-transitionend
        // на слайдах, но мы подавили transitions. Диспатчим вручную — так же,
        // как это делает effectVirtualTransitionEnd в Swiper.
        swiper.slides.forEach((slide) => {
          const event = new window.CustomEvent('transitionend', {
            bubbles: true,
            cancelable: true,
            propertyName: 'transform'
          })
          Object.defineProperty(event, 'propertyName', { value: 'transform' })
          this.#slideTransformTarget(slide).dispatchEvent(event)
        })
      }
    }

    this.#animationId = requestAnimationFrame(animate)
  }

  /**
   * Отменяет текущую RAF-анимацию навигации, если она запущена.
   *
   * @returns {void}
   */
  #cancelAnimation() {
    if (this.#animationId !== null) {
      cancelAnimationFrame(this.#animationId)
      this.#animationId = null
    }
    this.#isAnimating = false
  }

  /**
   * Кубическая ease-out кривая для анимации навигации.
   *
   * Быстрый старт → tuck пикует на t≈0.2 → карточка овершутится за пределы
   * финального положения → плавно укладывается. Визуально воспроизводит
   * «вытащить из колоды → уложить», как при перетаскивании мышью.
   *
   * @param {number} t Нормализованное время [0, 1].
   * @returns {number} Значение кривой в точке t.
   */
  #easeOutCubic(t) {
    return 1 - Math.pow(1 - t, 3)
  }

  /**
   * Лениво импортирует Swiper и его модули.
   *
   * Результат кешируется на классе, чтобы несколько экземпляров контроллера
   * не загружали один и тот же bundle повторно.
   *
   * @returns {Promise<Object>} Объект с `Swiper` и модулями Swiper.
   */
  async #importSwiper() {
    if (!BenefitsSliderController.#swiperCache) {
      const [{ default: Swiper }, modules] = await Promise.all([
        import('swiper'),
        // eslint-disable-next-line import/no-unresolved
        import('swiper/modules')
      ])
      BenefitsSliderController.#swiperCache = { Swiper, ...modules }
    }
    return BenefitsSliderController.#swiperCache
  }
}
